import '../../../scss/hotfood/components/hotfood-fable.scss'

import {
  Body2,
  Box,
  Button,
  ButtonGroup,
  Flex,
  Grid,
  GridItem,
  Label1,
  Link,
} from '@sainsburys-tech/fable'
import { AlertCircleFilled, Tick } from '@sainsburys-tech/icons'
import React, { useEffect, useState } from 'react'

import ActionBanner from '../../../common/components/Banners/ActionBanner'
import { getUserStore } from '../../../utils/localStore'
import { FormErrorMessageKey } from '../../enums/FormErrorMessageKey'
import { TempCheckCounterStatus } from '../../enums/TempCheckCounterStatus'
import * as TemperatureCheckPosition from '../../enums/TemperatureCheckPosition'
import { ICounterProps } from '../../types/ICounterProps'
import { ITemperatureCheckValidationProps } from '../../types/ITemperatureCheckValidationProps'
import { ITemperatureData } from '../../types/ITemperatureData'
import { validateTemperatures } from '../../utils/hotFoodUtils'
import TemperatureCheckErrorMessage from '../common/TemperatureCheckErrorMessage'
import CounterTempCheckInputCard from './CounterTempCheckInputCard'

const CounterCard = (props: ICounterProps) => {
  const [temperatures, setTemperatures] = useState<ITemperatureData[]>([
    { position: 1, temperature: '' },
    { position: 2, temperature: '' },
    { position: 3, temperature: '' },
    { position: 4, temperature: '' },
  ])
  const [validation, setValidation] =
    useState<ITemperatureCheckValidationProps>({
      errorData: [],
      isLowReadingWarned: false,
      messageKey: FormErrorMessageKey.NONE,
      result: true,
    })

  useEffect(() => {
    const maxShelfNumber = Math.max(
      ...(props.shelves?.map((shelf) => shelf.shelfNumber) || []),
    )

    if (maxShelfNumber === 5) {
      setTemperatures([
        { position: 1, temperature: '' },
        { position: 2, temperature: '' },
        { position: 3, temperature: '' },
        { position: 4, temperature: '' },
        { position: 5, temperature: '' },
      ])
    } else {
      setTemperatures([
        { position: 1, temperature: '' },
        { position: 2, temperature: '' },
        { position: 3, temperature: '' },
        { position: 4, temperature: '' },
      ])
    }
  }, [props.shelves, props.isBluetoothReading])

  const onChangeFaultyOrFixed = () => {
    props.handleFaultyOrFixed({
      counterNumber: props.counterNumber,
      isFaulty: !props.isFaulty,
      shelves: props.shelves,
      storeId: getUserStore().storeId,
    })
  }

  const onSave = async () => {
    const [errorData, messageKey, result] = validateTemperatures(temperatures)
    setValidation({
      errorData: errorData as ITemperatureData[],
      isLowReadingWarned:
        !validation.isLowReadingWarned &&
        !result &&
        messageKey === FormErrorMessageKey.Under20DegTemperature,
      messageKey: messageKey as FormErrorMessageKey,
      result: result as boolean,
    })

    if (!result && !validation.isLowReadingWarned) {
      return
    }
    await props.handleSubmit(
      props.counterNumber,
      temperatures,
      props.isBluetoothReading,
    )
  }

  const renderButtons = () => (
    <ButtonGroup
      align='left'
      gap='lg'
      orientation='horizontal'
      style={{ marginTop: '16px', width: '100%' }}
    >
      <Button
        onClick={() => onSave()}
        testid='submit-readings-button'
        isFullWidth={false}
      >
        Submit readings
      </Button>
      <Button
        onClick={handleClear}
        testid='cancel-button'
        variant='secondary'
        className='c-counter-cancel-button'
        style={{
          backgroundColor:
            'var(--fable-color-background-secondary-default), #ffffff',
        }}
        isFullWidth={false}
      >
        Cancel
      </Button>
    </ButtonGroup>
  )

  const handleClear = () => {
    const maxShelfNumber =
      props.shelves && props.shelves.length > 0
        ? Math.max(...props.shelves.map((shelf) => shelf.shelfNumber))
        : 4

    const shelfEnumMap = [
      TemperatureCheckPosition.Shelf.Top,
      TemperatureCheckPosition.Shelf.Second,
      TemperatureCheckPosition.Shelf.Third,
      TemperatureCheckPosition.Shelf.Fourth,
      TemperatureCheckPosition.Shelf.Fifth,
    ]

    const clearedTemps = Array.from({ length: maxShelfNumber }, (_, i) => ({
      position: shelfEnumMap[i],
      temperature: '',
    }))
    setTemperatures(clearedTemps)
    setValidation({
      errorData: [],
      isLowReadingWarned: false,
      messageKey: FormErrorMessageKey.NONE,
      result: true,
    })
  }
  const renderCardHeader = () => (
    <Flex
      direction='row'
      alignItems='center'
      justifyContent='space-between'
      gap='lg'
      style={{ width: '100%' }}
      shouldWrap={true}
    >
      <Flex direction='row' alignItems='center' gap='sm'>
        <span>
          <Label1>{props.title}</Label1>
        </span>
        {props.isFaulty && (
          <AlertCircleFilled
            className='c-error-icon-counter'
            style={{ color: 'var(--fable-color-text-error)' }}
          />
        )}
      </Flex>
      {props.isFaulty && (
        <span
          data-testid='faulty-text'
          style={{
            flex: 1,
            marginLeft: '1rem',
            marginRight: '1rem',
          }}
        >
          <Body2>
            This cabinet has been marked as faulty, DO NOT USE until fixed. To
            report the problem or check on the progress of the work order please
            visit&nbsp;
            <Link
              onClick={() =>
                (window.location.href =
                  'https://fmassist.sainsburys.co.uk/fmassist_landing')
              }
              className='c-link-black'
            >
              FM Assist
            </Link>
            .
          </Body2>
        </span>
      )}
      {props.isFaulty && (
        <Button
          onClick={() => onChangeFaultyOrFixed()}
          testid='cabinet-fixed-button'
          isFullWidth={false}
          variant='secondary'
          style={{
            backgroundColor:
              'var(--fable-color-background-secondary-default), #ffffff',
          }}
        >
          Mark as fixed
        </Button>
      )}
    </Flex>
  )
  return (
    <Box
      className='c-counter-card-content'
      data-testid='cabinet-card'
      backgroundColor='secondary'
    >
      <Flex direction='column' gap='md' shouldWrap={true}>
        {props.tempCheckStatus === TempCheckCounterStatus.Checked &&
        props.isFaulty ? (
          <div className='c-batch-card'>{renderCardHeader()}</div>
        ) : (
          renderCardHeader()
        )}
        {props.tempCheckStatus === TempCheckCounterStatus.Ready &&
          !props.isFaulty && (
            <>
              <Box style={{ width: '100%' }} padding='none'>
                <Grid
                  cols={{
                    xs: 1,
                    sm: 2,
                    md: temperatures.length,
                    lg: temperatures.length,
                  }}
                  gap={{ sm: 'sm', md: 'lg', lg: 'lg' }}
                  data-testid='cabinet-card-body'
                  style={{
                    width: '100%',
                  }}
                >
                  {temperatures.map((item, index) => (
                    <GridItem key={index} colSpan={1}>
                      <Flex direction='row' alignItems='center' gap='lg'>
                        <Label1 className='c-counter-temp-label'>
                          {(() => {
                            switch (item.position) {
                              case 1:
                                return 'Top'
                              case 2:
                                return '2nd'
                              case 3:
                                return '3rd'
                              case 4:
                                return '4th'
                              case 5:
                                return '5th'
                              default:
                                return ''
                            }
                          })()}
                        </Label1>
                        <CounterTempCheckInputCard
                          counterNumber={props.counterNumber}
                          hasError={
                            validation.errorData.filter(
                              (t) => t.position === item.position,
                            ).length > 0
                          }
                          currentTemp={item}
                          temperatures={temperatures}
                          setTemperatures={setTemperatures}
                          isModal={false}
                          isFirstCheck={props.isFirstCheck}
                          isBluetoothReading={props.isBluetoothReading}
                          className='c-counter-temp-input'
                        />
                      </Flex>
                    </GridItem>
                  ))}
                </Grid>
              </Box>
              <Flex
                className='c-counter-card-buttons-wrapper'
                direction='row'
                alignItems='center'
                justifyContent='space-between'
                style={{ width: '100%' }}
              >
                {renderButtons()}
                <Button
                  variant='tertiary'
                  className='c-counter-card-faulty-button'
                  onClick={() => onChangeFaultyOrFixed()}
                  testid='faulty-button'
                  style={{
                    paddingLeft: '6px',
                    paddingRight: '6px',
                    width: '280px',
                  }}
                >
                  Mark cabinet as faulty
                </Button>
              </Flex>

              {!validation.result && (
                <TemperatureCheckErrorMessage
                  class='hotfood-font-bold'
                  messageKey={validation.messageKey}
                />
              )}
            </>
          )}
        {props.tempCheckStatus === TempCheckCounterStatus.Checked &&
          !props.isFaulty && (
            <ActionBanner
              message={props.title}
              icon={<Tick />}
              className='c-common-banner--success'
            />
          )}
      </Flex>
    </Box>
  )
}

export default CounterCard
